<script>
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import KeyboardButton from "./KeyboardButton.svelte";
  import WordDisplay from "./WordDisplay.svelte";

  import { finished, setupGame, attempts, attemptIndex, saveGame, pauseGame } from "../store/game";
  import { fiveLetterWords } from "../store/words";
  
  const dispatch = createEventDispatcher()
  const day = new Date().getDate()
  const wordsOfMonth = ['habit','hacks','lapel','lambs','lairs','quasi','yelps','wakes','zesty','waged','vapes','quirk','tacky','waltz','venue','zeros','yacht','tacts','quant','ulcer','octad','garbs','egret','decoy','tamer','baled','gaffs','jaded','oboes','tacos','wager']
  let wordOfDay = wordsOfMonth[day]
  let exactLetters = []
  let containLetters = []
  let trashLetters = []

  const acceptableKeys = ['p','o','i','u','y','t','r','e','w','q','l','k','j','h','g','f','d','s','a','m','n','b','v','c','x','z','Backspace','Enter',]
  const isWord = async(w, skipChecking = false) => {
    try {
      if (!skipChecking)
        $attempts[$attemptIndex].status = 'checking'
      return $fiveLetterWords.findIndex(word => word === w) !== -1
    } catch (error) {
      return false
    }
  }
  const handleInput = async(event) => {
    if ($finished) return
    if (event.detail === 'enter') {
      if ($attempts[$attemptIndex].word.length === 5) {
        const word = await isWord($attempts[$attemptIndex].word)
        if(word) {
          $attempts[$attemptIndex].status = 'saved'
          if ($attempts[$attemptIndex].word === wordOfDay) {
            dispatch('success')
            $finished = true
          }
          $attemptIndex += 1
        } else {
          $attempts[$attemptIndex].status = 'fail'
          setTimeout(() => {
            $attempts[$attemptIndex].status = ''
          }, 100);
        }
      }
      saveGame($finished)
    } else if (event.detail === 'delete' || event.detail === 'backspace') {
      let split = $attempts[$attemptIndex].word.split('')
      split.pop()
      $attempts[$attemptIndex].word = split.join('')
    } else if ($attempts[$attemptIndex].word.length < 5) {
      $attempts[$attemptIndex].word += event.detail
      if ($attempts[$attemptIndex].word.length === 5) {
        const word = await isWord($attempts[$attemptIndex].word, true)
        if(!word) {
          $attempts[$attemptIndex].status = 'fail'
          setTimeout(() => {
            $attempts[$attemptIndex].status = ''
          }, 200);
        }
      }
    }
  }

  const addExactLetters = (ev) => {
    exactLetters = [...exactLetters, ev.detail]
  }
  const addContainsLetters = (ev) => {
    containLetters = [...containLetters, ev.detail]
  }
  const addTrashLetters = (ev) => {
    trashLetters = [...trashLetters, ev.detail]
  }
  const handleKeyDown = (ev) => {
    if (acceptableKeys.includes(ev.key))
      handleInput({ detail: ev.key.toLowerCase() })
  }
  onMount(() => {
    window.addEventListener('keydown', handleKeyDown)
  })
  onDestroy(() => {
    pauseGame()
    window.removeEventListener('keydown', handleKeyDown)
  })
  $: hash = location.hash ? location.hash.split('#')[1] : ''
  $: {
    if (hash) {
      const wordWithSalt = window.atob(hash)
      const word = wordWithSalt.split('+')[0]
      wordOfDay = word.toLowerCase()
    }
    setupGame(hash)
  }
  </script>

  <div class="flex flex-col max-w-md mx-auto h-full">
    <div class="flex-grow"></div>
    <div class="relative p-2">
      {#each $attempts as {word, status}}
        <WordDisplay
          challengeWord={wordOfDay}
          guess={word}
          status={status}
          on:exact-match={addExactLetters} 
          on:letter-found={addContainsLetters} 
          on:not-found={addTrashLetters} 
        />
      {/each}
    </div>
    <div class="flex-grow"></div>
    <div class="px-2">
      <div class="grid grid-cols-10 gap-1 max-w-[400px] mx-auto py-1">
        {#each ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'] as letter, i}
          <KeyboardButton value={letter} on:input={handleInput} exact={exactLetters} contains={containLetters} trash={trashLetters} />
        {/each}
      </div>
      <div class="grid grid-cols-9 gap-1 max-w-[400px] mx-auto py-1 px-4">
        {#each ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'] as letter, i}
          <KeyboardButton value={letter} on:input={handleInput} exact={exactLetters} contains={containLetters} trash={trashLetters} />
        {/each}
      </div>
      <div class="grid grid-cols-11 gap-1 max-w-[400px] mx-auto py-1">
        <div class="flex items-center justify-center uppercase rounded-sm text-gray-200 cursor-pointer col-span-2 bg-teal-700 mr-2" on:click={() => handleInput({ detail: 'enter'})}>
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-backspace text-2xl" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentcolor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M9 20v-8h-3.586a1 1 0 0 1 -.707 -1.707l6.586 -6.586a1 1 0 0 1 1.414 0l6.586 6.586a1 1 0 0 1 -.707 1.707h-3.586v8a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
          </svg>
        </div>
        {#each ['z', 'x', 'c', 'v', 'b', 'n', 'm'] as letter, i}
          <KeyboardButton value={letter} on:input={handleInput} exact={exactLetters} contains={containLetters} trash={trashLetters} />
        {/each}
        <div class="flex flex-col items-center justify-center uppercase rounded-sm text-gray-200 cursor-pointer col-span-2 bg-teal-700 ml-2" on:click={() => handleInput({ detail: 'delete'})}>
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-backspace text-3xl" width="1em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentcolor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M20 6a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-11l-5 -5a1.5 1.5 0 0 1 0 -2l5 -5z" />
            <path d="M12 10l4 4m0 -4l-4 4" />
          </svg>
        </div>
      </div>
    </div>
  </div>
  